import type { ReactNode } from 'react'
import { NavLink } from 'react-router'
import { useSesion } from '../auth/useSesion'
import { ConmutadorTema } from '../tema/ConmutadorTema'
import { IconoSalir } from './iconos'
import { DESTINOS } from './navegacion'

/**
 * Estructura de la aplicacion: barra lateral fija en escritorio y barra
 * inferior de iconos en movil. La version Streamlit ponia las pestanas en una
 * fila superior que se desbordaba lateralmente en el movil, asi que la mitad de
 * las secciones quedaban fuera de la vista. Aqui los cinco destinos estan
 * siempre visibles y al alcance del pulgar.
 *
 * El quinto (agendamiento) entra sin barrer a los otros: a 375 px la barra pasa
 * de cuatro celdas de 94 px a cinco de 75, que sigue por encima del objetivo
 * tactil comodo (`min-h-14` marca el alto, y 75 px de ancho es mas que los
 * 44x44 recomendados). Lo que no cabe es el texto largo, y por eso cada destino
 * lleva `etiquetaMovil`: ver el comentario de `navegacion.ts`.
 *
 * Sin emojis: los iconos vienen de `iconos.tsx`.
 *
 * Los colores salen de los tokens de `index.css` (`bg-superficie`, `text-tinta`,
 * ...), nunca de una escala de Tailwind directa: asi los dos temas se resuelven
 * solos y un componente nuevo no puede nacer solo-claro por olvido.
 */

interface Props {
  children: ReactNode
}

const claseEnlaceLateral = (activo: boolean) =>
  [
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    activo
      ? 'bg-acento-suave text-acento-tinta'
      : 'text-tinta-2 hover:bg-elevado hover:text-tinta',
  ].join(' ')

const claseEnlaceMovil = (activo: boolean) =>
  [
    // min-h-14: objetivo tactil comodo, por encima del minimo recomendado.
    // `whitespace-nowrap`: antes que partir "Logistica" en dos lineas, que
    // descuadraria el alto de una sola celda, el rotulo se mantiene entero.
    // Cabe porque `etiquetaMovil` esta acotada a diez caracteres.
    'flex min-h-14 flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-medium whitespace-nowrap transition-colors',
    activo ? 'text-acento-tinta' : 'text-tinta-apagada',
  ].join(' ')

export function AppShell({ children }: Props) {
  const { sesion, salir } = useSesion()
  const correo = sesion?.user?.email ?? ''

  return (
    <div className="min-h-screen bg-lienzo text-tinta">
      {/* Escritorio: lateral fija. Oculta por debajo de md. */}
      <aside className="hidden md:fixed md:inset-y-0 md:left-0 md:flex md:w-64 md:flex-col md:border-r md:border-borde md:bg-superficie">
        <div className="px-5 py-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-tinta-apagada">
            Panel tactico
          </p>
          <p className="mt-1 text-lg font-semibold text-tinta">Aguas de Castellon</p>
        </div>

        <nav aria-label="Navegacion principal" className="flex-1 space-y-1 px-3">
          {DESTINOS.map(({ ruta, etiqueta, Icono, IconoActivo }) => (
            <NavLink key={ruta} to={ruta} className={({ isActive }) => claseEnlaceLateral(isActive)}>
              {({ isActive }) => (
                <>
                  {isActive ? (
                    <IconoActivo className="size-5 shrink-0" />
                  ) : (
                    <Icono className="size-5 shrink-0" />
                  )}
                  <span>{etiqueta}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-borde px-5 py-4">
          {/* El conmutador va junto al boton de salir: son las dos cosas que se
              hacen "a la cuenta", no a los datos. */}
          <ConmutadorTema />

          <p className="mt-4 truncate text-sm text-tinta-2" title={correo}>
            {correo}
          </p>
          <button
            type="button"
            onClick={() => void salir()}
            className="mt-2 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado hover:text-tinta"
          >
            <IconoSalir className="size-5 shrink-0" />
            <span>Cerrar sesion</span>
          </button>
        </div>
      </aside>

      {/* El hueco de la lateral se reserva en el envoltorio, no en el propio
          main: asi el ancho maximo del contenido se centra en el espacio que
          queda libre y no se descuadra en pantallas muy anchas.
          pb-24 en movil: la barra inferior es fija y taparia la ultima fila. */}
      <div className="md:pl-64">
        {/* Movil: la lateral esta oculta, asi que el conmutador necesita sitio
            propio. Va arriba y **no** es fija: la barra inferior ya ocupa
            pantalla de forma permanente con los cinco destinos, y una segunda
            franja fija dejaria el contenido en un tunel. Al desplazarse se va,
            que es lo que se espera de un control que se toca una vez al mes. */}
        <div className="flex items-center justify-between gap-3 px-4 pt-4 md:hidden">
          <p className="text-sm font-semibold text-tinta">Aguas de Castellon</p>
          <ConmutadorTema rotuloVisible={false} />
        </div>

        <main className="mx-auto max-w-7xl px-4 pb-24 pt-6 md:px-8 md:pb-10">{children}</main>
      </div>

      {/* Movil: barra inferior con los destinos, sin scroll lateral. La rejilla
          reparte el ancho exacto de la pantalla (una columna por destino), asi
          que ninguna celda puede empujar a la de al lado fuera del viewport. A
          seis destinos y 375 px cada celda mide 62 px, suficiente para el icono
          y un rotulo corto (por eso Calendario abrevia a "Calend."). */}
      <nav
        aria-label="Navegacion principal movil"
        style={{ gridTemplateColumns: `repeat(${DESTINOS.length}, minmax(0, 1fr))` }}
        className="fixed inset-x-0 bottom-0 z-40 grid border-t border-borde bg-superficie pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {DESTINOS.map(({ ruta, etiquetaMovil, Icono, IconoActivo }) => (
          <NavLink key={ruta} to={ruta} className={({ isActive }) => claseEnlaceMovil(isActive)}>
            {({ isActive }) => (
              <>
                {isActive ? (
                  <IconoActivo className="size-6 shrink-0" />
                ) : (
                  <Icono className="size-6 shrink-0" />
                )}
                {/* Sin `aria-label` con el nombre largo: el nombre accesible
                    tiene que contener el texto visible (WCAG 2.5.3), o quien
                    dicta por voz diria "Conversaciones" y no pasaria nada. */}
                <span>{etiquetaMovil}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
